import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";

function BlogHeader() {
  return (
    <header className="h-16 bg-sp-navy flex items-center justify-between px-6">
      <Link to="/"><Logo dark /></Link>
      <nav className="flex items-center gap-5 text-sm text-white/70">
        <Link to="/platform" className="hover:text-white hidden sm:block">Platform</Link>
        <Link to="/pricing" className="hover:text-white hidden sm:block">Pricing</Link>
        <Link to="/blog" className="text-sp-orange">Blog</Link>
        <Link to="/login" className="bg-sp-orange hover:bg-sp-orangedark text-white rounded-full px-4 py-1.5">Sign in</Link>
      </nav>
    </header>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const [meta, setMeta] = useState({ categories: [], tags: [] });
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useSeo({
    title: "Blog — Construction insights, guides & product updates",
    description: "Practical guides on BOQ, budgeting, procurement, RERA and construction management for Indian builders and homeowners — from the Sitepar team.",
    jsonLd: { "@context": "https://schema.org", "@type": "Blog", name: "Sitepar Blog", url: "https://sitepar.com/blog" },
  });

  const load = () => {
    const params = {};
    if (cat !== "All") params.category = cat;
    if (q) params.q = q;
    api.get("/blog/posts", { params }).then(({ data }) => setPosts(data));
  };
  useEffect(() => { api.get("/blog/meta").then(({ data }) => setMeta(data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [cat, q]);

  return (
    <div className="min-h-screen bg-white">
      <BlogHeader />
      <div className="bg-sp-navy text-white">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <p className="text-xs uppercase tracking-widest text-sp-orange font-semibold">Sitepar Blog</p>
          <h1 className="text-4xl sm:text-5xl font-bold mt-3">Build smarter.<br />Insights for how India builds.</h1>
          <div className="relative mt-6 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <input data-testid="blog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…"
              className="w-full h-11 pl-10 pr-4 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-white/40 outline-none focus:border-sp-orange" />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex flex-wrap gap-2 mb-8">
          {["All", ...meta.categories].map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`blog-cat-${c}`}
              className={`text-sm rounded-full px-4 py-1.5 border ${cat === c ? "bg-sp-orange text-white border-sp-orange" : "border-slate-200 text-slate-600 hover:border-sp-orange"}`}>{c}</button>
          ))}
        </div>
        {!posts ? <div className="py-20 text-center text-slate-400">Loading…</div> :
          posts.length === 0 ? <div className="py-20 text-center text-slate-400" data-testid="blog-empty">No articles yet. Check back soon.</div> :
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p) => (
                <button key={p.id} data-testid={`blog-card-${p.slug}`} onClick={() => nav(`/blog/${p.slug}`)} className="text-left rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow group">
                  <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                    {p.cover_image ? <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> :
                      <div className="w-full h-full bg-sp-navy flex items-center justify-center"><span className="text-white/30 text-sm">{p.category}</span></div>}
                  </div>
                  <div className="p-5">
                    <span className="text-xs text-sp-orange font-semibold">{p.category}</span>
                    <h3 className="font-bold text-sp-navy mt-1 leading-snug line-clamp-2">{p.title}</h3>
                    <p className="text-sm text-slate-500 mt-2 line-clamp-2">{p.excerpt}</p>
                    <div className="text-xs text-slate-400 mt-3">{p.author} · {p.read_time} min read</div>
                  </div>
                </button>
              ))}
            </div>}
      </div>
      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-400">© {new Date().getFullYear()} Sitepar · The Construction Operating System</footer>
    </div>
  );
}
